import React from 'react';

export const Privacy: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
      
      <div className="prose prose-lg max-w-none">
        <p className="text-gray-600 mb-6">
          <strong>Effective Date:</strong> January 15, 2025
        </p>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Welcome to AskiiMall ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our marketplace platform for AI services.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
          
          <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Personal Information</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            We collect information that you provide directly to us, including:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>Account registration information (name, email address, password)</li>
            <li>Profile information (company name, contact details, profile picture)</li>
            <li>Payment information (processed securely through third-party payment processors)</li>
            <li>Communication data (support requests, feedback, correspondence)</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">2.2 Automatically Collected Information</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            When you use our platform, we automatically collect:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>Usage data (pages visited, features used, time spent on platform)</li>
            <li>Device information (IP address, browser type, operating system)</li>
            <li>Log data (access times, error logs, performance metrics)</li>
            <li>Cookies and similar tracking technologies</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">2.3 OAuth and Third-Party Services</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            When you authenticate using Google OAuth or connect third-party services:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>Basic profile information (name, email, profile picture)</li>
            <li>OAuth tokens and authorization credentials (encrypted and securely stored)</li>
            <li>Service usage data from connected AI services</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            We use the collected information for:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>Providing and maintaining our marketplace platform</li>
            <li>Processing transactions and managing subscriptions</li>
            <li>Authenticating users and securing accounts</li>
            <li>Connecting and managing third-party AI service integrations</li>
            <li>Sending service updates, security alerts, and support messages</li>
            <li>Analyzing usage patterns to improve our services</li>
            <li>Preventing fraud and ensuring platform security</li>
            <li>Complying with legal obligations and enforcing our terms</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Sharing and Disclosure</h2>
          
          <h3 className="text-xl font-semibold text-gray-800 mb-3">4.1 Service Providers</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            We share information with trusted third-party service providers who assist us in:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>Payment processing (Stripe, GetLago)</li>
            <li>Cloud infrastructure and hosting (AWS, Google Cloud Platform)</li>
            <li>Authentication services (AWS Cognito, Google OAuth)</li>
            <li>Analytics and monitoring tools</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">4.2 AI Service Providers</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            When you use AI services through our marketplace, we share necessary information with the respective service providers to enable the integration and functionality you've subscribed to.
          </p>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">4.3 Legal Compliance</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            We may disclose your information if required by law, legal process, or to protect the rights, property, or safety of AskiiMall, our users, or the public.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            We implement industry-standard security measures to protect your information:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>Encryption in transit (TLS/SSL) and at rest (AES-256)</li>
            <li>OAuth token encryption and secure credential storage</li>
            <li>Regular security audits and vulnerability assessments</li>
            <li>Access controls and authentication mechanisms</li>
            <li>Monitoring and logging of security events</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mb-4">
            However, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security of your data.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            We retain your personal information for as long as necessary to:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>Provide our services and maintain your account</li>
            <li>Comply with legal obligations and resolve disputes</li>
            <li>Enforce our agreements and protect our legitimate interests</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mb-4">
            When you delete your account, we will delete or anonymize your personal data within 90 days, except where retention is required by law.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Your Privacy Rights</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Depending on your location, you may have the following rights:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Correction:</strong> Update or correct inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your personal data</li>
            <li><strong>Portability:</strong> Receive your data in a portable format</li>
            <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
            <li><strong>Revoke consent:</strong> Withdraw OAuth authorizations and service connections</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mb-4">
            To exercise these rights, contact us at <a href="mailto:support@askiimall.ai" className="text-blue-600 hover:underline">support@askiimall.ai</a>.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Cookies and Tracking Technologies</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            We use cookies and similar technologies for:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>Authentication and session management</li>
            <li>User preferences and settings</li>
            <li>Analytics and performance monitoring</li>
            <li>Security and fraud prevention</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mb-4">
            You can control cookies through your browser settings, but disabling cookies may affect platform functionality.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Children's Privacy</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Our services are not intended for users under the age of 18. We do not knowingly collect information from children. If you believe we have collected information from a child, please contact us immediately.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. International Data Transfers</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy and applicable laws.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Third-Party Links</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Our platform may contain links to third-party websites and services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes to This Privacy Policy</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            We may update this Privacy Policy from time to time. We will notify you of material changes by:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>Posting the updated policy on our website</li>
            <li>Updating the "Effective Date" at the top</li>
            <li>Sending email notifications for significant changes</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mb-4">
            Your continued use of our services after changes indicates acceptance of the updated policy.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Contact Us</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            If you have questions, concerns, or requests regarding this Privacy Policy or our data practices:
          </p>
          <div className="bg-gray-50 p-6 rounded-lg">
            <p className="text-gray-700 mb-2">
              <strong>Email:</strong> <a href="mailto:support@askiimall.ai" className="text-blue-600 hover:underline">support@askiimall.ai</a>
            </p>
            <p className="text-gray-700 mb-2">
              <strong>Website:</strong> <a href="https://askiimall.ai" className="text-blue-600 hover:underline">https://askiimall.ai</a>
            </p>
            <p className="text-gray-700">
              <strong>AskiiMall, Inc.</strong>
            </p>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. GDPR Compliance (EU Users)</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            For users in the European Union, we comply with the General Data Protection Regulation (GDPR):
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li><strong>Legal basis:</strong> We process data based on consent, contract performance, legal obligations, and legitimate interests</li>
            <li><strong>Data Protection Officer:</strong> Contact us for DPO inquiries</li>
            <li><strong>Right to lodge a complaint:</strong> You may file a complaint with your local data protection authority</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. CCPA Compliance (California Users)</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            For California residents, under the California Consumer Privacy Act (CCPA):
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>We do not sell your personal information</li>
            <li>You have the right to opt-out of data sales (if applicable)</li>
            <li>You have the right to non-discrimination for exercising your privacy rights</li>
            <li>Contact us to exercise your CCPA rights</li>
          </ul>
        </section>

        <div className="border-t border-gray-200 pt-8 mt-12">
          <p className="text-gray-600 text-sm">
            Last Updated: January 15, 2025
          </p>
          <p className="text-gray-600 text-sm mt-2">
            © 2025 AskiiMall, Inc. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

